import { useEffect, useRef, useState } from "react";
import { Reorder } from "framer-motion";
import {
  ArrowCounterClockwise,
  Eraser,
  CaretLeft,
  CaretRight,
  Cube,
  DotsSixVertical,
  Eye,
  EyeSlash,
  Star,
  X,
} from "@phosphor-icons/react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { HD_STATUS_LABEL } from "@/lib/photoHd";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Small corner tag: pulsing while the HD pass runs, "HD" once it has landed.
const HdBadge = ({ p, testPrefix }) => {
  const status = p.enhance_status;
  if (p.enhanced && status !== "pending") {
    return (
      <span
        data-testid={`${testPrefix}-hd-${p.id}`}
        title={`Enhanced to ${p.enhance_size || "HD"}${p.enhance_mode ? ` · ${p.enhance_mode}` : ""}`}
        className="pointer-events-none absolute left-1 top-1 z-20 rounded bg-emerald-500 px-1.5 py-0.5 text-[9px] font-black uppercase tracking-widest text-black"
      >
        HD
      </span>
    );
  }
  if (!HD_STATUS_LABEL[status]) return null;
  return (
    <span
      data-testid={`${testPrefix}-hd-${p.id}`}
      title={p.enhance_error || ""}
      className={`pointer-events-none absolute left-1 top-1 z-20 rounded px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-widest ${
        status === "pending"
          ? "animate-pulse bg-indigo-500 text-white"
          : "bg-zinc-800/90 text-zinc-300"
      }`}
    >
      {HD_STATUS_LABEL[status]}
    </span>
  );
};

export const PhotoGallery = ({
  photos,
  onRemove,
  onEdit,
  onCropShape,
  onReorder,
  onToggleWebsite,
  onToggleEdge,
  onRevertHd,
  onCutout,
  onSetCover,
  angleOptions,
  onAngle,
  testPrefix = "photo",
}) => {
  const [open, setOpen] = useState(null);
  const coverId = onSetCover && photos[0] ? photos[0].id : null;
  const [showOriginal, setShowOriginal] = useState(false);
  const [items, setItems] = useState(photos);
  const itemsRef = useRef(photos);
  const token = localStorage.getItem("sw_token");
  const src = (p) => `${API}/photos/${p.storage_path}?auth=${token}`;
  const originalSrc = (p) => `${API}/photos/${p.original_path}?auth=${token}`;

  useEffect(() => {
    setItems(photos);
    itemsRef.current = photos;
  }, [photos]);

  useEffect(() => {
    setShowOriginal(false);
  }, [open]);

  const step = (delta) =>
    setOpen((i) =>
      i === null ? null : (i + delta + photos.length) % photos.length,
    );
  const openLightbox = (p) => setOpen(photos.findIndex((x) => x.id === p.id));

  const controls = (p) => (
    <>
      <HdBadge p={p} testPrefix={testPrefix} />
      {onSetCover &&
        (p.id === coverId ? (
          <span
            data-testid={`${testPrefix}-cover-badge-${p.id}`}
            className="pointer-events-none absolute left-1 top-8 z-20 flex items-center gap-1 rounded bg-amber-400 px-1.5 py-0.5 text-[9px] font-black uppercase tracking-widest text-zinc-950"
          >
            <Star size={10} weight="fill" /> Cover
          </span>
        ) : (
          <button
            type="button"
            data-testid={`${testPrefix}-set-cover-${p.id}`}
            onPointerDown={(e) => e.stopPropagation()}
            onClick={() => onSetCover(p)}
            title="Use as the cover photo"
            className="absolute left-1 top-8 z-20 rounded-full bg-black/70 p-1 text-white transition-colors duration-150 hover:bg-amber-400 hover:text-black"
          >
            <Star size={12} weight="bold" />
          </button>
        ))}
      {onRevertHd && p.enhanced && p.original_path && (
        <button
          type="button"
          data-testid={`${testPrefix}-revert-hd-${p.id}`}
          onPointerDown={(e) => e.stopPropagation()}
          onClick={() => onRevertHd(p)}
          title="Undo HD — put the camera original back"
          className="absolute right-1 top-7 z-20 rounded-full bg-black/70 p-1 text-white transition-colors duration-150 hover:bg-amber-500 hover:text-black"
        >
          <ArrowCounterClockwise size={12} weight="bold" />
        </button>
      )}
      {onCutout && (
        <button
          type="button"
          data-testid={`${testPrefix}-cutout-${p.id}`}
          onPointerDown={(e) => e.stopPropagation()}
          onClick={() => onCutout(p)}
          title={p.cutout ? "Restore original background" : "Remove background"}
          className={`absolute left-1 bottom-1 z-20 rounded-full p-1 transition-colors duration-150 ${p.cutout ? "bg-amber-500 text-black hover:bg-amber-400" : "bg-black/70 text-white hover:bg-amber-500 hover:text-black"}`}
        >
          <Eraser size={12} weight="bold" />
        </button>
      )}
      {angleOptions && onAngle && (
        <select
          data-testid={`${testPrefix}-angle-${p.id}`}
          value={p.angle || ""}
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
          onChange={(e) => onAngle(p, e.target.value)}
          title="Which side of the sink is this photo?"
          className={`absolute bottom-1 left-1 z-20 h-6 max-w-[calc(100%-0.5rem)] cursor-pointer appearance-none rounded-full px-2 text-[9px] font-bold uppercase tracking-widest text-white outline-none transition-colors duration-150 ${
            p.angle ? "bg-indigo-600/90 hover:bg-indigo-500" : "bg-black/70 hover:bg-zinc-700"
          }`}
        >
          <option value="">Angle?</option>
          {angleOptions.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      )}
      {onEdit && (
        <button
          type="button"
          data-testid={`${testPrefix}-edit-${p.id}`}
          onPointerDown={(e) => e.stopPropagation()}
          onClick={() => onEdit(p)}
          className="absolute bottom-1 left-1 z-20 rounded-full bg-black/70 px-2 py-1 text-[9px] font-bold uppercase tracking-widest text-white transition-colors duration-150 hover:bg-yellow-500 hover:text-black"
        >
          Edit
        </button>
      )}
      {p.shape && (
        <span
          data-testid={`${testPrefix}-shape-badge-${p.id}`}
          className="pointer-events-none absolute left-1 top-1 z-20 rounded bg-amber-400 px-1.5 py-0.5 text-[9px] font-black uppercase tracking-widest text-zinc-950"
        >
          Shape
        </span>
      )}
      {onCropShape && !p.shape && (
        <button
          type="button"
          data-testid={`${testPrefix}-crop-shape-${p.id}`}
          onPointerDown={(e) => e.stopPropagation()}
          onClick={() => onCropShape(p)}
          title="Cut this photo to the traced usable shape"
          className={`absolute bottom-1 z-20 rounded-full bg-black/70 px-2 py-1 text-[9px] font-bold uppercase tracking-widest text-white transition-colors duration-150 hover:bg-amber-400 hover:text-black ${
            onEdit ? "left-12" : "left-1"
          }`}
        >
          Shape
        </button>
      )}
      <button
        type="button"
        data-testid={`${testPrefix}-remove-${p.id}`}
        onPointerDown={(e) => e.stopPropagation()}
        onClick={() => onRemove(p.id)}
        className="absolute right-1 top-1 z-20 rounded-full bg-black/70 p-1 text-white transition-colors duration-150 hover:bg-red-600"
      >
        <X size={12} weight="bold" />
      </button>
      {onToggleWebsite && (
        <button
          type="button"
          data-testid={`${testPrefix}-website-${p.id}`}
          onPointerDown={(e) => e.stopPropagation()}
          onClick={() => onToggleWebsite(p)}
          title={
            p.show_on_website === false
              ? "Hidden from the public website — click to show"
              : "Shown on the public website — click to hide"
          }
          className={`absolute bottom-1 right-1 z-20 rounded-full p-1 text-white transition-colors duration-150 ${
            p.show_on_website === false
              ? "bg-black/70 hover:bg-emerald-600"
              : "bg-emerald-600/90 hover:bg-emerald-500"
          }`}
        >
          {p.show_on_website === false ? (
            <EyeSlash size={12} weight="bold" />
          ) : (
            <Eye size={12} weight="bold" />
          )}
        </button>
      )}
      {onToggleEdge && p.show_on_website !== false && (
        <button
          type="button"
          data-testid={`${testPrefix}-edge-${p.id}`}
          onPointerDown={(e) => e.stopPropagation()}
          onClick={() => onToggleEdge(p)}
          aria-pressed={Boolean(p.edge_texture)}
          title={
            p.edge_texture
              ? "This flat photo is wrapped onto the website's 3D edge previews — click to unset"
              : "Use this flat photo for the website's 3D edge previews (instead of the first photo)"
          }
          className={`absolute bottom-1 right-8 z-20 flex h-6 items-center gap-1 rounded-full px-1.5 text-[9px] font-bold uppercase tracking-widest transition-colors duration-150 ${
            p.edge_texture
              ? "bg-yellow-500 text-black hover:bg-yellow-400"
              : "bg-black/70 text-white hover:bg-yellow-500 hover:text-black"
          }`}
        >
          <Cube size={11} weight="bold" /> Edge
        </button>
      )}
    </>
  );

  return (
    <>
      {onReorder && items.length > 0 ? (
        <Reorder.Group
          axis="x"
          as="div"
          values={items}
          onReorder={(next) => {
            setItems(next);
            itemsRef.current = next;
          }}
          className="mt-4 flex gap-3 overflow-x-auto pb-2"
          data-testid={`${testPrefix}-grid`}
        >
          {items.map((p) => (
            <Reorder.Item
              key={p.id}
              value={p}
              as="div"
              onDragEnd={() => onReorder(itemsRef.current.map((x) => x.id))}
              whileDrag={{ scale: 1.04, zIndex: 10 }}
              className="group relative w-40 shrink-0 cursor-grab overflow-hidden rounded-md border border-zinc-800 active:cursor-grabbing"
              data-testid={`${testPrefix}-item-${p.id}`}
            >
              <div className="pointer-events-none absolute left-1 top-1 z-20 rounded bg-black/55 p-0.5 text-white opacity-0 transition-opacity duration-150 group-hover:opacity-100">
                <DotsSixVertical size={14} weight="bold" />
              </div>
              <img
                src={src(p)}
                alt={p.original_filename || "Photo"}
                draggable={false}
                data-testid={`${testPrefix}-thumb-${p.id}`}
                onClick={() => openLightbox(p)}
                className={`h-24 w-full cursor-pointer object-cover ${
                  p.show_on_website === false ? "opacity-40" : ""
                }`}
              />
              {controls(p)}
            </Reorder.Item>
          ))}
        </Reorder.Group>
      ) : (
        <div
          className="mt-4 grid grid-cols-3 gap-3 sm:grid-cols-4"
          data-testid={`${testPrefix}-grid`}
        >
          {photos.length === 0 && (
            <p className="col-span-full text-sm text-zinc-500">
              No photos yet — snap one from your phone.
            </p>
          )}
          {photos.map((p, i) => (
            <div
              key={p.id}
              className="group relative overflow-hidden rounded-md border border-zinc-800"
            >
              <button
                type="button"
                data-testid={`${testPrefix}-thumb-${p.id}`}
                onClick={() => setOpen(i)}
                className="block w-full"
              >
                <img
                  src={src(p)}
                  alt={p.original_filename || "Slab photo"}
                  className={`h-24 w-full object-cover transition-transform duration-150 group-hover:scale-105 ${
                    p.show_on_website === false ? "opacity-40" : ""
                  }`}
                />
              </button>
              {controls(p)}
            </div>
          ))}
        </div>
      )}

      <Dialog open={open !== null} onOpenChange={(v) => !v && setOpen(null)}>
        <DialogContent
          data-testid={`${testPrefix}-lightbox`}
          className="max-w-4xl border-zinc-800 bg-zinc-950 p-3"
        >
          {open !== null && photos[open] && (
            <div className="relative">
              <img
                data-testid={`${testPrefix}-lightbox-image`}
                src={
                  showOriginal && photos[open].original_path
                    ? originalSrc(photos[open])
                    : src(photos[open])
                }
                alt={photos[open].original_filename || "Slab photo"}
                className="max-h-[75vh] w-full rounded-md object-contain"
              />
              {photos[open].enhanced && photos[open].original_path && (
                <button
                  type="button"
                  data-testid={`${testPrefix}-lightbox-compare`}
                  onClick={() => setShowOriginal((v) => !v)}
                  className="absolute left-2 top-2 rounded-full bg-black/70 px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest text-white transition-colors duration-150 hover:bg-emerald-500 hover:text-black"
                >
                  {showOriginal ? "Showing original — see HD" : "HD — see original"}
                </button>
              )}
              {photos.length > 1 && (
                <>
                  <button
                    type="button"
                    data-testid={`${testPrefix}-lightbox-prev`}
                    onClick={() => step(-1)}
                    className="absolute left-2 top-1/2 -translate-y-1/2 rounded-full bg-black/70 p-3 text-white transition-colors duration-150 hover:bg-yellow-500 hover:text-black"
                  >
                    <CaretLeft size={18} weight="bold" />
                  </button>
                  <button
                    type="button"
                    data-testid={`${testPrefix}-lightbox-next`}
                    onClick={() => step(1)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full bg-black/70 p-3 text-white transition-colors duration-150 hover:bg-yellow-500 hover:text-black"
                  >
                    <CaretRight size={18} weight="bold" />
                  </button>
                </>
              )}
              <div className="mt-2 flex items-center justify-between text-xs text-zinc-500">
                <span className="truncate">
                  {photos[open].original_filename}
                </span>
                <span className="font-mono-data">
                  {open + 1} / {photos.length}
                </span>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
